/** Sidebar + topbar, exact de indeling en teksten uit de stijlreferentie. */
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { NAV, SUB } from '../nav'
import { useAiStand } from '../lib/aiStatus'
import { isVolledigBeheer, magRoute, useMij } from '../lib/rechten'
import { magMassaPagina } from '../lib/massaRechten'
import { useApi, usePoll } from '../lib/useApi'
import { useNieuweBerichten, type WachtRij } from '../lib/nieuweBerichten'
import { bucketPanels, type QueueRow } from '../lib/panels'
import { uitloggen } from '../lib/api'
import { Modal } from './Modal'
import { meldOffline, useHartslag } from '../lib/aanwezigheid'
import { instanceConfig } from '../lib/instance'
import { WachtwoordWijzigen } from './WachtwoordWijzigen'
import { installeerScrollDoorgeven } from '../lib/scrollDoorgeven'
import { InactiviteitBalk } from './InactiviteitBalk'
import { platformLabel, sindsTijd } from '../lib/verbinding'
import { eur as eurBedrag } from '../lib/format'
import { SCHAAL_KEUZES, useSchaalKeuze, useToepasSchaal } from '../lib/schaal'
import { HelpKnop, IntroTour } from './IntroTour'
import { VerplichtBericht, useMijnBerichten } from './VerplichtBericht'

/** Twee letters voor het rondje in de zijbalk, uit de naam van de gebruiker. */
function initialen(mij: { display_name?: string | null; username?: string } | null | undefined): string {
  const naam = String(mij?.display_name || mij?.username || '').trim()
  if (!naam) return '?'
  const delen = naam.split(/[\s._-]+/).filter(Boolean)
  const twee = delen.length >= 2 ? delen[0][0] + delen[1][0] : naam.slice(0, 2)
  return twee.toUpperCase()
}


/** 22-10-2026 (Jordy): een betaalde verkoop zonder chatter (vangnet). */
/** 23-10-2026: een Mass Message die stilviel (GET /api/admin/mass-auto/stopped). */
interface GestopteMm {
  id: number; platform: string; model_name: string | null; created_at: string | null
  recipient_count: number; sent_count: number; reason: string
}

/** 30-10-2026 (Jordy): een auditregel die niet geschreven kon worden (GET /api/admin/chatter-finance/audit-failures). */
interface AuditFout {
  at: string; action: string; resource_type: string; resource_id: number | null
  admin_user_id: number | null; error: string
}

interface OnbeheerdeVerkoop {
  id: number
  created_at: string
  platform: string
  event_type: string
  amount: number
  model: string | null
  conversation_id: number | null
  reden: string | null
}

export function Layout({ children }: { children: ReactNode }) {
  const merk = instanceConfig()
  const { pathname } = useLocation()
  const route = pathname.replace(/^\//, '') || 'dashboard'
  // Subroutes (bv. /conversations/show/17364) horen onder hun hoofdpagina,
  // anders staat de ruwe route in de kop.
  const baseRoute = route.split('/')[0]
  const meta = NAV.find((n) => n.route === baseRoute)
  // Het chatvenster is het enige scherm dat exact één viewport hoog moet zijn.
  const chatvenster = /^\/conversations\/show\//.test(pathname)
  // De stand van de AI-bot hoort altijd in beeld te staan. Wie niet ziet dat
  // de bot aan staat, kan ook niet merken dat hij per ongeluk aan staat.
  const ai = useAiStand()
  // Eerste laag: verbergen wat deze gebruiker niet mag. De tweede laag is de
  // backend, die het met een 403 weigert — verbergen alleen is geen
  // beveiliging, want een adres is zo getypt.
  const mij = useMij()
  // 03-10-2026 (Jordy): scrollen loopt door naar de pagina als een paneel op
  // zijn eind zit (zie lib/scrollDoorgeven).
  useEffect(() => installeerScrollDoorgeven(), [])
  // 18-10-2026 (Jordy): het dashboard op de schaal van zijn screenshot, per
  // gebruiker aan te passen (zie lib/schaal). De keuze staat in de topbalk:
  // Settings is alleen voor beheer, en ook een chatter moet 100% kunnen kiezen.
  useToepasSchaal(baseRoute === 'f2f-login')
  const [schaalKeuze, zetSchaalKeuze] = useSchaalKeuze()
  // 30-09-2026: elke minuut een heartbeat voor ingelogde chatters (zie lib/aanwezigheid).
  useHartslag(mij.data?.linked_chatter_id != null)
  /** De menu-items die deze gebruiker mag zien. Zolang `/me` nog niet binnen
   *  is, is dit leeg — liever een halve seconde een lege zijbalk dan een menu
   *  dat openflitst met dingen die je niet mag. */
  const zichtbaar = useMemo(() => {
    const uit: typeof NAV = []
    let sectie: string | undefined
    for (const n of NAV) {
      if (n.section) sectie = n.section
      if (!magRoute(mij.data, n.route)) continue
      // 05-10-2026 (Jordy): Mass Messages alleen met de schuif (manager) of als
      // owner/admin; zie lib/massaRechten.ts.
      if (n.route === 'mass-messages' && !magMassaPagina(mij.data)) continue
      uit.push({ ...n, section: sectie })
      sectie = undefined
    }
    return uit
  }, [mij.data])
  // 09-09 (Jordy): de taalknop is weg. Hij zette alleen `dashboard_lang` in
  // localStorage; geen enkele component las die waarde, dus het scherm bleef
  // in beide standen hetzelfde. Een knop die niets doet is erger dan geen
  // knop — je gaat ervan uit dat de tekst die je ziet een keuze was. Het
  // dashboard is Engels; scripts/i18n_scan.mjs en
  // tests/test_geen_hardcoded_nederlands.py houden dat zo.
  const [notifOpen, setNotifOpen] = useState(false)
  /* Onder 1100px staat de zijbalk buiten beeld (`left:-260px`). Er was geen
     enkele knop die hem terughaalde, dus op een telefoon of tablet was er
     geen navigatie: je kwam op Dashboard binnen en daar bleef je. */
  const [menuOpen, setMenuOpen] = useState(false)
  // 03-10-2026 (Jordy): zijbalk in te klappen tot pictogrammen; in deze
  // browser onthouden.
  const [smal, setSmal] = useState<boolean>(() => {
    try { return localStorage.getItem('zijbalk.smal') === '1' } catch { return false }
  })
  const zetSmal = (aan: boolean) => {
    setSmal(aan)
    try { localStorage.setItem('zijbalk.smal', aan ? '1' : '0') } catch { /* privé-venster */ }
  }
  const [wachtwoordOpen, setWachtwoordOpen] = useState(false)
  const [uitOpen, setUitOpen] = useState(false)

  /** Badges in de zijbalk. 03-10-2026 (Jordy): alleen nog bij Chats, en dan
   *  het aantal open chats zoals de Chats-pagina het telt ("N open chats"),
   *  niet het aantal kandidaatrijen (tot 350). Models, Chatters en Fans
   *  krijgen geen getal meer. */
  const queue = useApi<{ data: WachtRij[] }>(
    '/api/admin/conversations?limit=350&offset=0&skip_total=true' +
    '&queue_candidates=true&sort_order=desc&panel=unanswered&dashboard_blocked=false')
  // 28-10-2026: de server stuurt {account_blocked, notices}, geen lijst. Elke
  // 20 s: een manager wacht op de leesbevestiging (bel en schermvullend
  // bericht delen deze ene aanvraag).
  const notices = useMijnBerichten()
  usePoll(20000, notices.refetch)
  const alerts = useApi<{ chatter_name: string; alert_active: boolean }[]>(
    '/api/admin/chatter-finance/shift-alerts')

  // 04-10-2026 (Jordy): modellen (binnen wat deze gebruiker ziet) waarvan de
  // chatverbinding langer dan de drempel weg is. Licht endpoint; alleen
  // gemeten storingen, een model zonder meting staat er niet in.
  const verbinding = useApi<{ stale: { model_slug: string; stage_name: string;
    platform: string; stale_since: string | null }[] }>(
    '/api/admin/conversations/connection-status')

  // 22-10-2026 (Jordy): vangnet. Betaalde content zonder chatter (owner/admin);
  // een gemiste koppeling mag nooit stil doorglippen.
  const eigenaar = ['owner', 'admin'].includes(String(mij.data?.role ?? '').toLowerCase())
  const onbeheerd = useApi<{ items: OnbeheerdeVerkoop[]; total: number }>(
    eigenaar ? '/api/admin/dashboard/earnings/onbeheerd' : null, [eigenaar])
  // 23-10-2026 (Jordy): een Mass Message die stilviel (bijv. de F2F-identiteitscontrole) meldt zich.
  const gestopt = useApi<{ items: GestopteMm[]; total: number }>(
    eigenaar ? '/api/admin/mass-auto/stopped' : null, [eigenaar])
  // 30-10-2026 (Jordy): de actie ging door, maar de auditregel ontbreekt.
  const auditFout = useApi<{ items: AuditFout[]; total: number }>(
    eigenaar ? '/api/admin/chatter-finance/audit-failures' : null, [eigenaar])

  // De bundle ververst deze tellers periodiek; hier elke 60 s.
  usePoll(60000, () => {
    alerts.refetch(); verbinding.refetch()
    if (eigenaar) { onbeheerd.refetch(); gestopt.refetch(); auditFout.refetch() }
  })
  // De wachtrij apart en sneller: hij voedt zowel het cijfer in de zijbalk als
  // de meldingen rechtsonder. Elke 15 seconden — vaak genoeg om een nieuw
  // bericht te merken, en licht genoeg om niet de hele dag 350 gesprekken op
  // te halen.
  usePoll(15000, queue.refetch)
  useNieuweBerichten(queue.data?.data)

  const badges = useMemo<Record<string, string>>(() => {
    const open = queue.data?.data
      ? bucketPanels(queue.data.data as unknown as QueueRow[]).open.length
      : 0
    return {
      conversations: open ? String(open) : '',
      posting: 'LIVE',
    }
  }, [queue.data])

  const verbindingWeg = verbinding.data?.stale ?? []
  const onbeheerdeVerkopen = eigenaar ? (onbeheerd.data?.items ?? []) : []
  const gestopteMms = eigenaar ? (gestopt.data?.items ?? []) : []
  const auditFouten = eigenaar ? (auditFout.data?.items ?? []) : []
  const noticeCount = (notices.data?.notices?.length ?? 0) +
    (alerts.data ?? []).filter((a) => a.alert_active).length +
    verbindingWeg.length + onbeheerdeVerkopen.length + gestopteMms.length + auditFouten.length

  return (
    <div className={smal ? 'app smal' : 'app'}>
      {/* 28-10-2026: bericht dat pas weggaat na lezen, en het blokkeerscherm. */}
      <VerplichtBericht mijn={notices} />
      <aside className={menuOpen ? 'sidebar open' : 'sidebar'}
             onClick={() => setMenuOpen(false)}>
        <div className="brand">
          {/* Merknaam komt uit de instantie-instellingen: dezelfde code
              draait straks ook voor Temptation Talent. */}
          <div className="logo">{merk.initials}</div>
          <div>
            <b>{merk.brand}</b>
            <span>{merk.subtitle}</span>
          </div>
        </div>
        <nav className="nav">
          {/* Een kopje zonder items eronder is een lege regel in de zijbalk;
              daarom hangt het kopje aan het eerste item dat wél getoond wordt. */}
          {zichtbaar.map((n, i) => (
            <div key={n.route}>
              {n.section && (i === 0 || zichtbaar[i - 1].section !== n.section)
                ? <div className="sec">{n.section}</div> : null}
              <NavLink
                to={`/${n.route}`}
                className={({ isActive }) => (isActive ? 'on' : '')}
              >
                <span className="ic" title={smal ? n.label : undefined}>{n.icon}</span>
                <span className="label">{n.label}</span>
                {badges[n.route] ? <span className="badge">{badges[n.route]}</span> : null}
              </NavLink>
            </div>
          ))}
        </nav>
        <button type="button" className="inklapknop"
                title={smal ? 'Expand menu' : 'Collapse menu'}
                aria-label={smal ? 'Expand menu' : 'Collapse menu'}
                onClick={(e) => { e.stopPropagation(); zetSmal(!smal) }}>
          {smal ? '»' : '«'}
        </button>
        {/* Wie er is ingelogd. Hier stond "System Admin / koksjordy · owner"
            LETTERLIJK in de regel. Iedereen zag dus de naam en de rol van de
            eigenaar, ook een chatter — en dat maakte het bij een melding
            onmogelijk te zien met welk account iemand keek. Nu uit
            /api/admin/auth/me, dezelfde bron als de rechten. */}
        <div className="who">
          <div className="av">{initialen(mij.data)}</div>
          <div>
            <b style={{ fontSize: 13 }}>
              {mij.data?.display_name || mij.data?.username || '—'}
            </b>
            <small>
              {mij.data ? `${mij.data.username} · ${mij.data.role}` : 'not logged in'}
            </small>
          </div>
        </div>
        {/* Welke build er draait. Zonder dit is bij een melding niet te zeggen
            of iemand de nieuwe versie al ziet of nog een oude uit de cache. */}
        <div className="bouwstempel" title="Commit and build time of this version">
          build {__BOUWSTEMPEL__}
        </div>
      </aside>

      {menuOpen ? (
        <div className="menusluier" onClick={() => setMenuOpen(false)} />
      ) : null}
      <div className="main">
        <header className="topbar">
          <button className="menuknop" type="button" aria-label="Menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((v) => !v)}>☰</button>
          <div>
            <h1>{meta?.label ?? route}</h1>
            <div className="sub">{SUB[route] ?? ''}</div>
          </div>
          <div className="spacer" />
          <select className="select sm schaalkeuze" aria-label="Dashboard size" data-tour="dashboard-size"
                  title="Dashboard size (saved for your account)"
                  value={schaalKeuze} onChange={(e) => zetSchaalKeuze(e.target.value)}>
            {SCHAAL_KEUZES.map((k) => (
              <option key={k.value} value={k.value}>Size: {k.value === 'auto' ? 'Auto' : k.label}</option>
            ))}
          </select>
          {/* De stand van de AI-bot, altijd zichtbaar. Rood is hier de rustige
              kleur: uit betekent dat er niets vanzelf naar een fan gaat. */}
          <NavLink to="/settings"
                   className={ai.data?.effective ? 'aistand aan' : 'aistand uit'}
                   title={ai.data?.effective
                     ? `AI chatbot is ON for ${ai.data?.conversations_ai_on ?? 0} chat(s)`
                     : 'AI chatbot is off — nothing is sent to fans automatically'}>
            <span className="stip" />
            AI {ai.data?.effective ? 'ON' : 'OFF'}
          </NavLink>
          <div className="iconbtn" title="Notifications" onClick={() => setNotifOpen((v) => !v)}>
            🔔{noticeCount ? <span className="dot" /> : null}
            {/* 2-11-2026 (Jordy): een ontbrekende auditregel valt op, ook met de bel dicht. */}
            {auditFouten.length ? (
              <span className="dot" data-testid="bel-audit-teken" title="Audit record missing"
                    style={{ background: 'var(--bad)', top: 'auto', bottom: 6 }} />
            ) : null}
          </div>
          {/* 18-10-2026 (Jordy): intro-tour opnieuw starten (components/IntroTour). */}
          <HelpKnop />
          <NavLink to="/settings" className="iconbtn" title="Settings">⚙️</NavLink>
          <div className="iconbtn" title="Change password" onClick={() => setWachtwoordOpen(true)}>
            🔑
          </div>
          <div
            className="iconbtn"
            title="Sign out"
            onClick={() => setUitOpen(true)}
          >
            ⎋
          </div>
        </header>
        <IntroTour />
        {wachtwoordOpen ? <WachtwoordWijzigen onClose={() => setWachtwoordOpen(false)} /> : null}
        {/* 03-10-2026 (Jordy): chatters na 1 uur stilte uitgelogd. */}
        <InactiviteitBalk aan={String(mij.data?.role ?? '').toLowerCase() === 'chatter'} />
        {uitOpen ? (
          // 03-10-2026 (Jordy): naast uitloggen hier ook "overal uitloggen" —
          // elke sessie van dit account, op elk apparaat, vervalt meteen.
          <Modal title="Sign out?" onClose={() => setUitOpen(false)}
                 desc="Sign out everywhere also ends your sessions on every other device and browser."
                 footer={<>
                   <button className="btn ghost sm" type="button" onClick={() => setUitOpen(false)}>Cancel</button>
                   {[false, true].map((overal) => (
                     <button key={String(overal)} className={overal ? 'btn ghost sm' : 'btn primary sm'} type="button"
                             onClick={async () => {
                               // 30-09-2026: eerst "offline" melden, anders blijft een chatter
                               // na uitloggen nog tot tien minuten als online staan.
                               if (mij.data?.linked_chatter_id != null) await meldOffline()
                               await uitloggen(overal)
                               window.location.reload()
                             }}>
                       {overal ? 'Sign out everywhere' : 'Sign out'}
                     </button>
                   ))}
                 </>}>
            {null}
          </Modal>
        ) : null}
        {notifOpen ? (
          <div className="content" style={{ paddingBottom: 0 }}>
            <div className="panel">
              <div className="head">
                <div>
                  <h3>Notifications</h3>
                  <p>{noticeCount} open</p>
                </div>
                <div className="r">
                  <button className="btn ghost sm" type="button" onClick={() => setNotifOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
              <div className="body flush">
                <div className="feed">
                  {/* 2-11-2026 (Jordy): auditfouten vastgepind bovenaan, zodat ze niet
                      wegzakken tussen de andere meldingen (owner/admin). */}
                  {auditFouten.map((f, i) => (
                    <div className="it" key={`af-${i}`} data-testid="melding-audit-fout"
                         style={{ borderLeft: '3px solid var(--bad)' }}>
                      <span className="st tag bad">Audit · pinned</span>
                      <div className="mut">
                        {sindsTijd(f.at)}: the action <b>{f.action}</b> ({f.resource_type}
                        {f.resource_id != null ? ` ${f.resource_id}` : ''}, user {f.admin_user_id ?? '?'}) went
                        through, but its audit record could not be written ({f.error}).
                      </div>
                    </div>
                  ))}
                  {verbindingWeg.map((v) => (
                    <div className="it" key={`vb-${v.model_slug}`}>
                      <span className="st tag bad">Connection</span>
                      <div className="mut">
                        {v.platform !== 'fancentro' && !isVolledigBeheer(mij.data) ? (
                          // 31-10-2026 (Jordy): bij F2F geen tijdstip voor andere rollen.
                          <>F2F sync for <b>{v.stage_name}</b> is delayed</>
                        ) : (
                          <>
                            {platformLabel(v.platform)} connection for <b>{v.stage_name}</b> is
                            down since {sindsTijd(v.stale_since)}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {gestopteMms.map((g) => (
                    <Link className="it" key={`mm-${g.id}`} to="/mass-messages" data-testid="melding-mm-gestopt"
                          onClick={() => setNotifOpen(false)}>
                      <span className="st tag bad">MM stopped</span>
                      <div className="mut">
                        {g.model_name ?? 'model'} · {platformLabel(g.platform)} · {sindsTijd(g.created_at)}:
                        Mass Message stopped with {g.sent_count} of {g.recipient_count} sent. {g.reason}
                      </div>
                    </Link>
                  ))}
                  {onbeheerdeVerkopen.map((v) => (
                    <Link className="it" key={`ob-${v.id}`} to="/earnings" data-testid="melding-onbeheerd"
                          onClick={() => setNotifOpen(false)}>
                      <span className="st tag bad">No chatter</span>
                      <div className="mut">
                        {eurBedrag(v.amount)} {v.model ?? 'model'} · {platformLabel(v.platform)} · {sindsTijd(v.created_at)}:
                        paid sale without a chatter. Assign it in Earnings.
                      </div>
                    </Link>
                  ))}
                  {(alerts.data ?? []).filter((a) => a.alert_active).map((a, i) => (
                    <div className="it" key={`al-${i}`}>
                      <span className="st tag bad">Shift</span>
                      <div className="mut">{a.chatter_name} is missing the shift attendance</div>
                    </div>
                  ))}
                  {(notices.data?.notices ?? []).map((n) => (
                    <div className="it" key={n.id}>
                      <span className="st tag acc">Notice</span>
                      <div>
                        <b>{n.created_by ? `From ${n.created_by}` : 'Notice'}</b>
                        <div className="mut" style={{ marginTop: 2 }}>{n.message}</div>
                      </div>
                    </div>
                  ))}
                  {!noticeCount ? <div className="empty">No notifications</div> : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <div className={chatvenster ? 'content chatscherm' : 'content'}>
          {/* Op elke pagina, want een dienst die niet bemand is raakt het hele
              team — behalve in het chatvenster. Daar moet alles binnen één
              scherm passen, en een balk die er soms wel en soms niet staat
              maakt het chatvak elke keer een andere hoogte. De melding blijft
              zichtbaar als toast rechtsonder. */}
          {!chatvenster && (alerts.data ?? []).some((a) => a.alert_active) ? (
            <div className="openbar" style={{ borderLeftColor: 'var(--bad)' }}>
              <b>⚠ Shift attendance alert</b>
              <span className="mut2">
                {(alerts.data ?? []).filter((a) => a.alert_active)
                  .map((a) => a.chatter_name).join(', ') || 'open shift'}
              </span>
            </div>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  )
}
